import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { LOGO_URL } from '@/lib/brand';
import { toast } from 'sonner';
import api from '@/lib/api';

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score += 25;
  if (password.length >= 12) score += 10;
  if (/[A-Z]/.test(password)) score += 25;
  if (/[a-z]/.test(password)) score += 10;
  if (/[0-9]/.test(password)) score += 20;
  if (/[^A-Za-z0-9]/.test(password)) score += 10;
  return Math.min(100, score);
}

function getStrengthLabel(score) {
  if (score < 25) return { label: 'Weak', color: 'text-red-500', bgColor: 'bg-red-500' };
  if (score < 50) return { label: 'Fair', color: 'text-amber-500', bgColor: 'bg-amber-500' };
  if (score < 75) return { label: 'Good', color: 'text-blue-500', bgColor: 'bg-blue-500' };
  return { label: 'Strong', color: 'text-emerald-500', bgColor: 'bg-emerald-500' };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No reset token provided');
      setValidating(false);
      return;
    }
    const validateToken = async () => {
      try {
        const res = await api.post('/auth/validate-reset-token', { token });
        if (res.data.valid) {
          setTokenValid(true);
          setTokenEmail(res.data.email || '');
        } else {
          setTokenError(res.data.reason || 'Invalid or expired token');
        }
      } catch (err) {
        setTokenError('Failed to validate token');
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const strength = getPasswordStrength(password);
  const strengthInfo = getStrengthLabel(strength);

  const checks = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One number', met: /[0-9]/.test(password) },
    { label: 'Passwords match', met: password && confirmPassword && password === confirmPassword },
  ];

  const allChecksMet = checks.every(c => c.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allChecksMet) {
      toast.error('Please meet all password requirements');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token, new_password: password });
      const { access_token, user } = res.data;
      localStorage.setItem('outplay_token', access_token);
      localStorage.setItem('outplay_user', JSON.stringify(user));
      setResetSuccess(true);
      toast.success('Password reset successful!');
      setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-loading">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
        <div className="relative flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !validating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-invalid">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
        <div className="relative w-full max-w-md">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-lg object-contain" />
              <span className="font-display font-bold text-2xl">Out-Play</span>
            </div>
          </div>
          <Card className="border shadow-lg">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <ShieldAlert className="h-7 w-7 text-destructive" />
              </div>
              <h2 className="text-lg font-display font-semibold">Invalid or Expired Link</h2>
              <p className="text-sm text-muted-foreground">
                {tokenError === 'Token expired' ? 'This password reset link has expired. Please request a new one.' :
                 tokenError === 'Token already used' ? 'This reset link has already been used. Request a new one if needed.' :
                 'This password reset link is invalid. Please request a new one.'}
              </p>
              <Link to="/login">
                <Button className="w-full" data-testid="reset-back-to-login">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-success">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
        <div className="relative w-full max-w-md">
          <Card className="border shadow-lg">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <ShieldCheck className="h-7 w-7 text-emerald-600" />
              </div>
              <h2 className="text-lg font-display font-semibold">Password Reset Successful!</h2>
              <p className="text-sm text-muted-foreground">Your password has been updated. Redirecting to dashboard...</p>
              <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm transition-colors duration-150">
            <ArrowLeft className="h-4 w-4" /> Back to login
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-2xl">Out-Play</span>
          </div>
        </div>
        <Card className="border shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-display">Set New Password</CardTitle>
            <CardDescription>
              {tokenEmail ? <>Resetting password for <strong>{tokenEmail}</strong></> : 'Create a strong new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    autoFocus
                  />
                  <button
                    type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Strength meter */}
                {password && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Password strength</span>
                      <span className={`text-xs font-medium ${strengthInfo.color}`}>{strengthInfo.label}</span>
                    </div>
                    <Progress value={strength} className="h-1.5" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="reset-confirm-password-input"
                  />
                  <button
                    type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Requirements checklist */}
              <div className="space-y-1.5 p-3 rounded-lg bg-muted/50">
                {checks.map((check) => (
                  <div key={check.label} className="flex items-center gap-2">
                    {check.met ? (
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
                    )}
                    <span className={`text-xs ${check.met ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>

              <Button type="submit" className="w-full" disabled={loading || !allChecksMet} data-testid="reset-password-submit-button">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</> : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
